"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { BookOpen, Lock, Sparkles, Waypoints } from "lucide-react";
import { EditorBackendProvider } from "@/lib/editor-backend/context";
import { demoEditorBackend } from "@/lib/demo/demo-editor-backend";
import { ChapterEditor } from "@/components/editor/chapter-editor";
import { AssistantSidebar } from "@/components/editor/assistant-sidebar";
import { useDemoStore } from "@/stores/demo-store";
import { useResizableSidebar } from "@/hooks/use-resizable-sidebar";
import { cn } from "@/lib/utils";
import { prepareEditorNavigation } from "@/lib/editor/navigation-guard";
import { DemoChapterList } from "./demo-chapter-list";
import { DemoMobileBar } from "./demo-mobile-bar";
import { DemoGateModal, type GateReason } from "./demo-gate-modal";
import { DemoTurnstile } from "./demo-turnstile";
import { DemoWiki } from "./demo-wiki";
import { useDemoPrompts } from "./use-demo-prompts";
import { captureClientEvent, trackRedditEvent } from "@/lib/analytics/client";
export function TryClient() {
  const $ = _c(99);
  const [workspace, setWorkspace] = useState("editor");
  const trackedDemoStart = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (trackedDemoStart.current) {
        return;
      }
      trackedDemoStart.current = true;
      captureClientEvent("demo_started", {
        landing_page: "/try"
      });
      trackRedditEvent("Lead");
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const mounted = useSyncExternalStore(_temp2, _temp3, _temp4);
  const storyId = useDemoStore(_temp5);
  const storyTitle = useDemoStore(_temp6);
  const chapters = useDemoStore(_temp7);
  const activeChapterId = useDemoStore(_temp8);
  const {
    used,
    remaining,
    limit,
    reachedLimit
  } = useDemoPrompts();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      initialWidth: 400,
      minWidth: 300
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    width: assistantWidth,
    isDragging,
    startDragging
  } = useResizableSidebar(t2);
  const [gate, setGate] = useState(null);
  const [limitDismissed, setLimitDismissed] = useState(false);
  const activeGate = gate ?? (reachedLimit && !limitDismissed ? "limit" : null);
  let t3;
  if ($[3] !== gate) {
    t3 = () => {
      if (gate) {
        setGate(null);
      } else {
        setLimitDismissed(true);
      }
    };
    $[3] = gate;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const closeGate = t3;
  if (!mounted) {
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex h-dvh items-center justify-center bg-background text-muted-foreground"><Sparkles className="mr-2 h-5 w-5 animate-pulse text-primary" />Loading your sandbox…</div>;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  let t4;
  if ($[6] !== activeChapterId || $[7] !== chapters) {
    t4 = chapters.find(c => c.id === activeChapterId) ?? chapters[0];
    $[6] = activeChapterId;
    $[7] = chapters;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const active = t4;
  if (!active) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex h-dvh flex-col items-center justify-center gap-3 bg-background px-6 text-center text-muted-foreground"><p>This sandbox has no chapters to show.</p><Link href="/signup/writer" className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-brand/30 transition hover:opacity-90">Create a free account</Link></div>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 bg-brand/10 px-3 py-1.5 text-center text-xs text-foreground sm:text-sm"><span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden={true} /><span><strong className="font-semibold">Demo mode</strong><span className="hidden sm:inline">{" "}— you're trying the editor.</span>{" "}Nothing here is saved. Refreshing clears your changes.</span></span><Link href="/signup/writer" className="shrink-0 whitespace-nowrap rounded-full bg-brand px-3 py-0.5 text-xs font-semibold text-white shadow-sm shadow-brand/30 transition hover:opacity-90">Create a free account</Link></div>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <BookOpen className="h-5 w-5 shrink-0 text-primary" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== storyTitle) {
    t7 = <span className="truncate font-semibold">{storyTitle}</span>;
    $[12] = storyTitle;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">(sample story)</span>;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7) {
    t9 = <div className="flex min-w-0 items-center gap-2">{t6}{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setWorkspace("editor");
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = workspace === "editor";
  const t12 = workspace === "editor" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground";
  let t13;
  if ($[18] !== t12) {
    t13 = cn("hidden items-center gap-1 rounded-md px-2 py-1 transition lg:flex", t12);
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <BookOpen className="h-3 w-3" />;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t11 || $[22] !== t13) {
    t15 = <button onClick={t10} aria-pressed={t11} className={t13}>{t14} Editor</button>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = async () => {
      if (await prepareEditorNavigation()) {
        setWorkspace("wiki");
      }
    };
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const t17 = workspace === "wiki";
  const t18 = workspace === "wiki" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground";
  let t19;
  if ($[25] !== t18) {
    t19 = cn("hidden items-center gap-1 rounded-md px-2 py-1 transition lg:flex", t18);
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Waypoints className="h-3 w-3" />;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t17 || $[29] !== t19) {
    t21 = <button onClick={t16} aria-pressed={t17} className={t19}>{t20} Wiki</button>;
    $[28] = t17;
    $[29] = t19;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  const t22 = remaining <= 5 ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground";
  let t23;
  if ($[31] !== t22) {
    t23 = cn("rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap", t22);
    $[31] = t22;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="hidden sm:inline">{" "}AI prompts</span>;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== limit || $[35] !== t23 || $[36] !== used) {
    t25 = <span className={t23} title="AI prompts used in this demo">{used} / {limit}{t24}</span>;
    $[34] = limit;
    $[35] = t23;
    $[36] = used;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setGate("plot");
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <button onClick={t26} className="hidden items-center gap-1 rounded-md px-2 py-1 text-muted-foreground transition hover:bg-muted hover:text-foreground lg:flex"><Lock className="h-3 w-3" />Plot Threads</button>;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== t15 || $[41] !== t21 || $[42] !== t25) {
    t28 = <div className="flex shrink-0 items-center gap-1 text-sm">{t15}{t21}{t25}{t27}</div>;
    $[40] = t15;
    $[41] = t21;
    $[42] = t25;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t28 || $[45] !== t9) {
    t29 = <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border bg-card/60 px-3 backdrop-blur-md sm:px-4">{t9}{t28}</header>;
    $[44] = t28;
    $[45] = t9;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  const t30 = workspace === "wiki" && "hidden";
  let t31;
  if ($[47] !== t30) {
    t31 = cn("relative flex flex-1 overflow-hidden", t30);
    $[47] = t30;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  const t32 = workspace === "wiki";
  let t33;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-sidebar lg:flex"><DemoChapterList /></aside>;
    $[49] = t33;
  } else {
    t33 = $[49];
  }
  let t34;
  if ($[50] !== active.authorNote || $[51] !== active.content || $[52] !== active.id || $[53] !== active.status || $[54] !== active.title || $[55] !== active.wordCount || $[56] !== storyId) {
    t34 = <main className="flex flex-1 flex-col overflow-hidden"><ChapterEditor key={active.id} chapterId={active.id} storyId={storyId} chapterTitle={active.title} authorNote={active.authorNote} initialContent={active.content} initialWordCount={active.wordCount} initialStatus={active.status} /></main>;
    $[50] = active.authorNote;
    $[51] = active.content;
    $[52] = active.id;
    $[53] = active.status;
    $[54] = active.title;
    $[55] = active.wordCount;
    $[56] = storyId;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  const t35 = isDragging && "editor-right-sidebar--dragging";
  let t36;
  if ($[58] !== t35) {
    t36 = cn("relative hidden shrink-0 flex-col border-l border-border bg-card/40 lg:flex", t35);
    $[58] = t35;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== assistantWidth) {
    t37 = {
      width: assistantWidth
    };
    $[60] = assistantWidth;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== startDragging) {
    t38 = <div className="editor-right-sidebar-resize-handle" onMouseDown={e => {
      e.preventDefault();
      startDragging();
    }} role="separator" aria-orientation="vertical" aria-label="Resize assistant panel" />;
    $[62] = startDragging;
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  let t39;
  if ($[64] !== active.id || $[65] !== storyId) {
    t39 = <AssistantSidebar storyId={storyId} chapterId={active.id} />;
    $[64] = active.id;
    $[65] = storyId;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] !== t36 || $[68] !== t37 || $[69] !== t38 || $[70] !== t39) {
    t40 = <aside className={t36} style={t37}>{t38}{t39}</aside>;
    $[67] = t36;
    $[68] = t37;
    $[69] = t38;
    $[70] = t39;
    $[71] = t40;
  } else {
    t40 = $[71];
  }
  let t41;
  if ($[72] !== t31 || $[73] !== t32 || $[74] !== t34 || $[75] !== t40) {
    t41 = <div className={t31} aria-hidden={t32}>{t33}{t34}{t40}</div>;
    $[72] = t31;
    $[73] = t32;
    $[74] = t34;
    $[75] = t40;
    $[76] = t41;
  } else {
    t41 = $[76];
  }
  const t42 = workspace === "wiki" ? "flex" : "hidden";
  let t43;
  if ($[77] !== t42) {
    t43 = cn("min-h-0 flex-1 flex-col", t42);
    $[77] = t42;
    $[78] = t43;
  } else {
    t43 = $[78];
  }
  const t44 = workspace !== "wiki";
  let t45;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <DemoWiki onOpenGate={setGate} />;
    $[79] = t45;
  } else {
    t45 = $[79];
  }
  let t46;
  if ($[80] !== t43 || $[81] !== t44) {
    t46 = <div className={t43} aria-hidden={t44}>{t45}</div>;
    $[80] = t43;
    $[81] = t44;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  let t48;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = () => setWorkspace("editor");
    t48 = async () => {
      if (await prepareEditorNavigation()) {
        setWorkspace("wiki");
      }
    };
    $[83] = t47;
    $[84] = t48;
  } else {
    t47 = $[83];
    t48 = $[84];
  }
  let t49;
  if ($[85] !== active.id || $[86] !== storyId || $[87] !== workspace) {
    t49 = <DemoMobileBar storyId={storyId} activeChapterId={active.id} onOpenGate={setGate} workspace={workspace} onOpenEditor={t47} onOpenWiki={t48} />;
    $[85] = active.id;
    $[86] = storyId;
    $[87] = workspace;
    $[88] = t49;
  } else {
    t49 = $[88];
  }
  let t50;
  if ($[89] !== activeGate || $[90] !== closeGate) {
    t50 = <DemoGateModal reason={activeGate} onClose={closeGate} />;
    $[89] = activeGate;
    $[90] = closeGate;
    $[91] = t50;
  } else {
    t50 = $[91];
  }
  let t51;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <DemoTurnstile />;
    $[92] = t51;
  } else {
    t51 = $[92];
  }
  let t52;
  if ($[93] !== t29 || $[94] !== t41 || $[95] !== t46 || $[96] !== t49 || $[97] !== t50) {
    t52 = <EditorBackendProvider backend={demoEditorBackend}><div className="flex h-dvh flex-col overflow-hidden bg-background">{t5}{t29}{t41}{t46}{t49}{t50}{t51}</div></EditorBackendProvider>;
    $[93] = t29;
    $[94] = t41;
    $[95] = t46;
    $[96] = t49;
    $[97] = t50;
    $[98] = t52;
  } else {
    t52 = $[98];
  }
  return t52;
}
function _temp8(s_2) {
  return s_2.activeChapterId;
}
function _temp7(s_1) {
  return s_1.chapters;
}
function _temp6(s_0) {
  return s_0.storyTitle;
}
function _temp5(s) {
  return s.storyId;
}
function _temp4() {
  return false;
}
function _temp3() {
  return true;
}
function _temp2() {
  return _temp;
}
function _temp() {}